import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { ERROR_403 } from 'common/config/api';
import { forbiddenPath } from 'common/config/urls';
import { useStore } from 'common/store';
import { Firm } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import CompGroups from 'pages/CompGroups/components/CompGroups';
import { CompGroup } from 'pages/CompGroups/components/CompGroups/CompGroups.props';
import { PRIVATE_TRANSACTIONS } from 'pages/CompGroups/constants/constants';
import { ExpandedState, TransactionsValidatedGridsState } from 'pages/Firms/Firms.props';
import generateCompGroupsRequest from 'pages/Firms/utilities/generateCompGroupsRequest';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { useResponse } from 'services/hooks';
import { useCreateOrUpdateCompGroups } from 'services/hooks/firm';

const CompGroupsPage = () => {
  const {firmSlugParam} = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const [{firmList, firmId, user}] = useStore() as unknown as UseStoreValues;
  const {errorNotification} = useResponse();

  const [GPCCompsGroups, setGPCCompsGroups] = useState<CompGroup[]>();
  const [transactionCompsGroups, setTransactionCompsGroups] = useState<CompGroup[]>();
  const [updatedCompGroups, createOrUpdateCompGroups] = useCreateOrUpdateCompGroups();
  const [compsToDelete, setCompsToDelete] = useState<CompGroup[]>([]);
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [transactionsValidatedGrids, setTransactionsValidatedGrids] = useState<TransactionsValidatedGridsState>({});
  const [showTransactionCellsWithErrors, setShowTransactionCellsWithErrors] = useState<boolean>(false);

  const renderSuperuserPage = useMemo(
    () => Boolean(user?.is_superuser && firmSlugParam === '0'),
    [user, firmSlugParam]
  );

  const saveCompGroups = useCallback(
    async (areCellsValid: boolean): Promise<void> => {
      if (!areCellsValid) {
        return;
      }
      const compGroupsList = [...(GPCCompsGroups || []), ...(transactionCompsGroups || [])];
      const validCompGroups = renderSuperuserPage
        ? compGroupsList
        : compGroupsList.filter(({is_global}) => !is_global);
      const validRequestBody = generateCompGroupsRequest(validCompGroups, compsToDelete, renderSuperuserPage);
      const firmIdentifier = renderSuperuserPage ? 0 : firmId;
      await createOrUpdateCompGroups?.(firmIdentifier ?? 0, validRequestBody);
    },
    [GPCCompsGroups, transactionCompsGroups, renderSuperuserPage, compsToDelete, firmId, createOrUpdateCompGroups]
  );

  const handleSave = useCallback((): Promise<void> => {
    if (isEmpty(GPCCompsGroups) && isEmpty(transactionCompsGroups) && isEmpty(compsToDelete)) {
      errorNotification('You must add at least one comp group to save.');
      return Promise.reject();
    }

    const compGroupsList = [...(GPCCompsGroups || []), ...(transactionCompsGroups || [])];
    const allCompGroupsHaveValidNames = compGroupsList.every(({name}) => name !== '');
    const allGPCCompGroupsHaveData = GPCCompsGroups?.every(group => group.gpc_comps?.length);
    const allPTCompGroupsHaveData = transactionCompsGroups?.every(group => group.pt_comps?.length);

    if (!allCompGroupsHaveValidNames || !allGPCCompGroupsHaveData || !allPTCompGroupsHaveData) {
      errorNotification('Check that the comp groups have valid names and are not empty');
      return Promise.reject();
    }

    if (!Object.values(transactionsValidatedGrids).every(({isValid}) => isValid)) {
      setShowTransactionCellsWithErrors(true);
      errorNotification('Check that the transactions table has valid data');
      return Promise.reject();
    }

    return saveCompGroups(true);
  }, [GPCCompsGroups, transactionCompsGroups, compsToDelete, transactionsValidatedGrids, saveCompGroups, errorNotification]);

  useEffect(() => {
    if (updatedCompGroups && !isUndefined(renderSuperuserPage)) {
      const updatedGroups = updatedCompGroups as CompGroup[];
      const filterCriteria = (group_type: string, requiredType: string, is_global: boolean) =>
        group_type === requiredType && (renderSuperuserPage ? is_global : !is_global);
      setGPCCompsGroups(
        updatedGroups.filter(({
          group_type,
          is_global
        }) => filterCriteria(group_type, PUBLIC_COMPANIES, is_global))
      );
      setTransactionCompsGroups(
        updatedGroups.filter(({group_type, is_global}) =>
          filterCriteria(group_type, PRIVATE_TRANSACTIONS, is_global)
        )
      );
      setCompsToDelete([]);
    }
    return () => {
      setGPCCompsGroups(undefined);
      setTransactionCompsGroups(undefined);
    };
  }, [updatedCompGroups, renderSuperuserPage]);

  const secondaryNavigation = useMemo(
    () => ({
      title: 'Comp Groups',
      actions: [
        {
          type: 'button' as const,
          key: 'comp-groups-save',
          label: 'Save',
          variant: 'positive' as const,
          onClick: handleSave,
        },
      ],
    }),
    [handleSave]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  const activeFirm = useMemo(() => {
    if (renderSuperuserPage || !firmSlugParam) {
      return undefined;
    }
    if (!firmList || (firmList as unknown as string) === ERROR_403) {
      return null;
    }
    return (firmList as Firm[]).find(item => item.slug === firmSlugParam);
  }, [firmList, firmSlugParam, renderSuperuserPage]);

  if (!renderSuperuserPage && isUndefined(activeFirm) && !isNull(user)) {
    navigate(forbiddenPath);
    return null;
  }

  return (
    <CompGroups
      GPCCompsGroups={GPCCompsGroups}
      setGPCCompsGroups={setGPCCompsGroups}
      transactionCompsGroups={transactionCompsGroups}
      setTransactionCompsGroups={setTransactionCompsGroups}
      setCompsToDelete={setCompsToDelete}
      expandedState={expandedState}
      setExpandedState={setExpandedState}
      setTransactionsValidatedGrids={setTransactionsValidatedGrids}
      showTransactionCellsWithErrors={showTransactionCellsWithErrors}
      firmId={renderSuperuserPage ? 0 : (firmId as number)}
    />
  );
};

export default CompGroupsPage;
